/**
 * Public OKDoc landing для сценария рекомендаций после приёма.
 * Дата: 17.03.2026
 */
import { useState } from 'react';
import { ExternalLink, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui';
import { AppointmentSummaryCard } from '@/features/okdoc/components/AppointmentSummaryCard';
import { RecommendationComponentGlyph, ScenarioSectionContent } from '@/features/okdoc/components/RecommendationSectionContent';
import { publicAppointmentActionsApi } from '@/features/okdoc/api/publicAppointmentActions';
import { isPatientAppStoreTarget, type OkdocBrandTheme, type OkdocPatientPortalConfig } from '@/features/okdoc/config';
import { getRecommendationsCTACopy, resolvePublicScenarioProfile } from '@/features/okdoc/utils/scenarioProfile';
import type { OkdocScenarioCTA, PublicAppointmentActionLanding } from '@/features/okdoc/types';

interface RecommendationsLandingProps {
  code: string;
  landing: PublicAppointmentActionLanding;
  theme: OkdocBrandTheme;
  onRedirect: (target?: string, options?: { openInNewTab?: boolean }) => void;
  patientPortal?: OkdocPatientPortalConfig;
}

// 19.03.26 2.: public-flow рекомендаций на общей scenario-profile основе.
// Связанный док: docs/living/okdoc/OKDOC_SCENARIOS_FOUNDATION.md
export function RecommendationsLanding({ code, landing, theme, onRedirect, patientPortal }: RecommendationsLandingProps) {
  const [acceptedCTA, setAcceptedCTA] = useState<string>('');
  const profile = resolvePublicScenarioProfile(landing);
  const ctaCopy = getRecommendationsCTACopy(profile);
  const ctas = (profile?.ctas || []).filter((cta) => !isPatientAppStoreTarget(cta.id || cta.target, cta.url));
  const sections = profile?.sections || [];
  const hasPortalFallbackCTA = ctas.length === 0 && Boolean(patientPortal?.url);
  // 03.04.26: информационные promo-offer лендинги не должны выглядеть
  // как персональное напоминание о визите. В admin preview source_context
  // может не прокидываться, поэтому опираемся на scenario_kind.
	const showAppointmentSummary = landing.scenario_kind !== 'promo_offer' && landing.scenario_kind !== 'contact_hub' && landing.scenario_kind !== 'birthday_offer';
  const shouldOpenCTAInNewTab = (cta?: NonNullable<typeof ctas>[number]) =>
    landing.scenario_kind === 'contact_hub' ||
    cta?.action_kind === 'contact_channel' ||
    cta?.action_kind === 'open_personal_account' ||
    cta?.interaction_intent === 'contact_navigation' ||
    cta?.interaction_intent === 'external_navigation';
  const isBookingCTA = (cta?: OkdocScenarioCTA) =>
    cta?.action_kind === 'book_now' || cta?.action_kind === 'call_clinic' || cta?.interaction_intent === 'booking_intent';
  const isGiftCTA = (cta?: OkdocScenarioCTA) =>
    cta?.action_kind === 'choose_gift' || cta?.interaction_intent === 'gift_choice';
  const handleCTA = (cta: OkdocScenarioCTA, index: number) => {
    const target = cta.target || cta.id || 'default';
    const key = cta.id || cta.target || `cta-${index}`;
    // Gift: фиксируем выбор без перехода в tel:/dialer (старые profile ещё могут отдавать tel:).
    if (isGiftCTA(cta)) {
      setAcceptedCTA(key);
      void fetch(publicAppointmentActionsApi.getTrackedRedirectUrl(code, target), {
        method: 'GET',
        redirect: 'manual',
        credentials: 'omit',
      }).catch(() => undefined);
      return;
    }
    if (!isBookingCTA(cta)) {
      onRedirect(target, { openInNewTab: shouldOpenCTAInNewTab(cta) });
      return;
    }
    setAcceptedCTA(key);
    window.setTimeout(() => {
      onRedirect(target, { openInNewTab: shouldOpenCTAInNewTab(cta) });
    }, 900);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_24px_60px_-36px_rgba(15,23,42,0.35)]">
        <div className="flex items-start gap-4">
          <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${theme.badge} text-white`}>
            <Sparkles className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            {profile?.badge && (
              <p className={`text-sm font-medium ${theme.accent}`}>{profile.badge}</p>
            )}
            <h2 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
              {profile?.title || (landing.scenario_kind === 'contact_hub' ? 'Остаёмся на связи' : 'Рекомендации после приёма')}
            </h2>
            {(profile?.subtitle || profile?.description) && (
              <p className="mt-3 text-sm leading-6 text-slate-600">
                {profile?.subtitle ? `${profile.subtitle}. ` : ''}
                {profile?.description}
              </p>
            )}
          </div>
        </div>
      </div>

      {showAppointmentSummary && <AppointmentSummaryCard landing={landing} />}

      {sections.map((section, index) => (
        <div
          key={section.id || `section-${index}`}
          className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_24px_60px_-36px_rgba(15,23,42,0.35)]"
        >
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-2xl ${theme.secondaryButton}`}>
              <RecommendationComponentGlyph component={section.component} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">{section.title || 'Полезный блок'}</h3>
              {section.description && (
                <p className="mt-1 text-sm leading-6 text-slate-600">{section.description}</p>
              )}
            </div>
          </div>
          <ScenarioSectionContent section={section} mode="public" />
        </div>
      ))}

      <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_24px_60px_-36px_rgba(15,23,42,0.35)]">
        <div className="flex items-center gap-3">
          <div className={`flex h-10 w-10 items-center justify-center rounded-2xl ${theme.badge} text-white`}>
            <ExternalLink className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{ctaCopy.title}</h3>
            {ctaCopy.description && <p className="mt-1 text-sm leading-6 text-slate-600">{ctaCopy.description}</p>}
          </div>
        </div>

        <div className="mt-5 space-y-3">
          {ctas.length > 0 ? (
            ctas.map((cta, index) => (
              <div key={cta.id || cta.target || `cta-${index}`} className="space-y-3">
                <Button
                  className={`w-full rounded-2xl px-5 py-4 text-base shadow-sm ${index === 0 ? theme.primaryButton : theme.secondaryButton}`}
                  size="lg"
                  variant={index === 0 ? 'primary' : 'secondary'}
                  onClick={() => handleCTA(cta, index)}
                  disabled={(!cta.url && !isGiftCTA(cta)) || acceptedCTA === (cta.id || cta.target || `cta-${index}`)}
                >
                  {cta.label || `Открыть материал ${index + 1}`}
                </Button>
                {acceptedCTA === (cta.id || cta.target || `cta-${index}`) && (
                  <div className="rounded-[24px] border border-emerald-100 bg-emerald-50 p-5 text-center text-sm leading-6 text-emerald-800">
                    {isGiftCTA(cta)
                      ? 'Спасибо! Ваш подарок зафиксирован. Администратор свяжется с Вами.'
                      : 'Спасибо! Ваша заявка принята и мы уже передали ее администратору!'}
                  </div>
                )}
              </div>
            ))
          ) : hasPortalFallbackCTA && patientPortal ? (
            <Button
              className={`w-full rounded-2xl px-5 py-4 text-base shadow-sm ${theme.primaryButton}`}
              size="lg"
              variant="primary"
              onClick={() => window.open(patientPortal.url, '_blank', 'noopener,noreferrer')}
            >
              {patientPortal.buttonLabel || 'Перейти в ЛК пациента'}
            </Button>
          ) : (
            <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4 text-sm text-amber-800">
              Для этого сценария пока не настроены CTA-кнопки. Код ссылки: {code}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
